import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode, type RefObject } from 'react';
import axios from '../utils/axiosInstance';
import { BASE_URL } from '@/config/constants';
import type { Socket } from 'socket.io-client';

interface ResourceUsage {
  current: number;
  limit: number;
  canAdd: boolean;
}

interface PlanUsage {
  showcases: ResourceUsage;
  images: ResourceUsage;
  banners: ResourceUsage;
  toursEvents: ResourceUsage;
  awards: ResourceUsage;
  themes: ResourceUsage;
  categories: ResourceUsage;
  contactLeadsAccess: { hasAccess: boolean };
  visitorAnalyticsAccess: { hasAccess: boolean };
}

interface PlanFeature {
  feature: string;
  pro: string;
  proMax: string;
  free?: string;
  paid?: string;
}

type PlanTier = 'unpaid' | 'pro' | 'pro_max';
type PlanLifecycle = 'onboarding' | 'pending_approval' | 'approved_unpublished' | 'published';

interface PlanCapabilities {
  websiteLayoutsAccess: boolean;
  customDomainAccess: boolean;
  managerInvitesAccess: boolean;
  contactLeadsAccess: boolean;
  visitorAnalyticsAccess: boolean;
  customCategoriesAccess: boolean;
}

interface PlanInfo {
  plan: PlanTier;
  effectivePlan: PlanTier;
  entitlementSource: 'payment' | 'promo' | 'admin' | 'legacy';
  planExpiresAt: string | null;
  billingCycle: 'monthly' | 'annual' | null;
  subscriptionStatus: string | null;
  subscriptionCurrentEnd: string | null;
  cancelAtCycleEnd: boolean;
  pendingChange: {
    changeType: string;
    sourcePlan: PlanTier;
    targetPlan: PlanTier;
    status: string;
    pendingChangeAt: string | null;
    currentPeriodEnd: string;
  } | null;
  isPublished: boolean;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  hasCompletedOnboarding: boolean;
  lifecycle: PlanLifecycle;
  onboardingDraft: boolean;
  priceCatalog: Record<'pro' | 'pro_max', { monthly: number; annual: number }>;
  capabilities: PlanCapabilities;
  planPrice: number;
  displayOriginalPrice: number | null;
  discountPercent: number;
  discountLabel: string | null;
  annualDiscountPercent: number;
  isInGracePeriod: boolean;
  gracePeriodDaysRemaining: number;
  gracePeriodEndsAt: string | null;
  usage: PlanUsage;
  features: PlanFeature[];
  defaultTheme: string | null;
}

interface PlanContextType {
  planInfo: PlanInfo | null;
  loading: boolean;
  error: string | null;
  isPaidPlan: boolean;
  isUnpaid: boolean;
  isPro: boolean;
  isProMax: boolean;
  lifecycle: PlanLifecycle | null;
  capabilities: PlanCapabilities;
  isInGracePeriod: boolean;
  isSuperAdmin: boolean;
  canAdd: (resourceType: keyof PlanUsage) => boolean;
  getUsage: (resourceType: keyof PlanUsage) => ResourceUsage | null;
  adjustUsage: (resourceType: keyof PlanUsage, delta: number) => void;
  isItemVisibleOnWebsite: (resourceType: keyof PlanUsage, index: number) => boolean;
  hasContactLeadsAccess: boolean;
  hasVisitorAnalyticsAccess: boolean;
  refreshPlanInfo: () => void;
  showUpgradeModal: boolean;
  setShowUpgradeModal: (show: boolean) => void;
  upgradeModalResource: string;
  openUpgradeModal: (resource?: string) => void;
  // Global "Connect custom domain" modal trigger — used after a paid upgrade
  // and from the Custom Domain settings tab.
  showCustomDomainModal: boolean;
  setShowCustomDomainModal: (show: boolean) => void;
  openCustomDomainModal: () => void;
}

const defaultPlanInfo: PlanInfo = {
  plan: 'unpaid',
  effectivePlan: 'unpaid',
  entitlementSource: 'legacy',
  planExpiresAt: null,
  billingCycle: null,
  subscriptionStatus: null,
  subscriptionCurrentEnd: null,
  cancelAtCycleEnd: false,
  pendingChange: null,
  isPublished: false,
  approvalStatus: 'pending',
  hasCompletedOnboarding: false,
  lifecycle: 'onboarding',
  onboardingDraft: true,
  priceCatalog: {
    pro: { monthly: 99, annual: 999 },
    pro_max: { monthly: 599, annual: 4999 },
  },
  capabilities: {
    websiteLayoutsAccess: false,
    customDomainAccess: false,
    managerInvitesAccess: false,
    contactLeadsAccess: false,
    visitorAnalyticsAccess: false,
    customCategoriesAccess: false,
  },
  planPrice: 599,
  displayOriginalPrice: null,
  discountPercent: 0,
  discountLabel: null,
  annualDiscountPercent: 20,
  isInGracePeriod: false,
  gracePeriodDaysRemaining: 0,
  gracePeriodEndsAt: null,
  usage: {
    showcases: { current: 0, limit: 5, canAdd: true },
    images: { current: 0, limit: 3, canAdd: true },
    banners: { current: 0, limit: 1, canAdd: true },
    toursEvents: { current: 0, limit: 1, canAdd: true },
    awards: { current: 0, limit: 1, canAdd: true },
    themes: { current: 0, limit: 1, canAdd: true },
    categories: { current: 0, limit: 0, canAdd: false },
    contactLeadsAccess: { hasAccess: false },
    visitorAnalyticsAccess: { hasAccess: false },
  },
  features: [],
  defaultTheme: null,
};

const PlanContext = createContext<PlanContextType | null>(null);

interface PlanProviderProps {
  children: ReactNode;
  domain: string;
  userType?: string;
  socketRef?: RefObject<Socket | null>;
}

export const PlanProvider: React.FC<PlanProviderProps> = ({ children, domain, userType, socketRef }) => {
  const isSuperAdmin = userType === 'super-admin';
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalResource, setUpgradeModalResource] = useState('');
  const [showCustomDomainModal, setShowCustomDomainModal] = useState(false);

  const fetchPlanInfo = useCallback(async () => {
    if (!domain) {
      setPlanInfo(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${BASE_URL}/api/domain/plan-info`);
      
      // For super-admin: fetch real plan info but override access permissions
      if (isSuperAdmin && response.data) {
        const realPlanInfo = response.data;
        // Override all usage to allow full access while keeping real counts
        const overriddenUsage = {
          showcases: { ...realPlanInfo.usage?.showcases, limit: -1, canAdd: true },
          images: { ...realPlanInfo.usage?.images, limit: -1, canAdd: true },
          banners: { ...realPlanInfo.usage?.banners, limit: -1, canAdd: true },
          toursEvents: { ...realPlanInfo.usage?.toursEvents, limit: -1, canAdd: true },
          awards: { ...realPlanInfo.usage?.awards, limit: -1, canAdd: true },
          themes: { ...realPlanInfo.usage?.themes, limit: -1, canAdd: true },
          categories: { ...realPlanInfo.usage?.categories, limit: -1, canAdd: true },
          contactLeadsAccess: { hasAccess: true },
          visitorAnalyticsAccess: { hasAccess: true },
        };
        setPlanInfo({ ...realPlanInfo, usage: overriddenUsage });
      } else {
        setPlanInfo(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching plan info:', err);
      setError(err.response?.data?.message || 'Failed to fetch plan info');
      setPlanInfo(defaultPlanInfo);
    } finally {
      setLoading(false);
    }
  }, [domain, isSuperAdmin]);

  useEffect(() => {
    fetchPlanInfo();
  }, [fetchPlanInfo]);

  // Listen for real-time plan updates via socket
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;

    const handlePlanUpdate = () => {
      console.log('[PlanContext] Received plan:update socket event, refreshing...');
      fetchPlanInfo();
    };

    socket.on('plan:update', handlePlanUpdate);
    return () => {
      socket.off('plan:update', handlePlanUpdate);
    };
  }, [socketRef, fetchPlanInfo]);

  const isUnpaid = planInfo?.effectivePlan === 'unpaid';
  const isPro = planInfo?.effectivePlan === 'pro';
  const isProMax = planInfo?.effectivePlan === 'pro_max';
  // Compatibility for existing unlimited/premium UI while consumers migrate.
  const isPaidPlan = isProMax || Boolean(planInfo?.onboardingDraft);
  const capabilities = planInfo?.capabilities || defaultPlanInfo.capabilities;
  const isInGracePeriod = planInfo?.isInGracePeriod || false;
  const hasContactLeadsAccess = planInfo?.usage?.contactLeadsAccess?.hasAccess || false;
  const hasVisitorAnalyticsAccess = planInfo?.usage?.visitorAnalyticsAccess?.hasAccess || false;

  const canAdd = (resourceType: keyof PlanUsage): boolean => {
    if (!planInfo?.usage) return true;
    const resource = planInfo.usage[resourceType];
    if ('canAdd' in resource) {
      return resource.canAdd;
    }
    if ('hasAccess' in resource) {
      return resource.hasAccess;
    }
    return true;
  };

  /**
   * Check if a list item at a given index (0-based, in order-sorted list) 
  * is within the current plan limit and will be visible on the website.
   */
  const isItemVisibleOnWebsite = (resourceType: keyof PlanUsage, index: number): boolean => {
    if (isSuperAdmin) return true;
    if (!planInfo?.usage) return true;
    if (isProMax || planInfo.onboardingDraft) return true;
    
    const resource = planInfo.usage[resourceType];
    // Boolean-locked features like categories
    if ('hasAccess' in resource) {
      return resource.hasAccess;
    }
    if ('limit' in resource) {
      const limit = (resource as ResourceUsage).limit;
      if (limit === -1) return true;
      return index < limit;
    }
    return true;
  };

  const getUsage = (resourceType: keyof PlanUsage): ResourceUsage | null => {
    if (!planInfo?.usage) return null;
    const resource = planInfo.usage[resourceType];
    if ('current' in resource) {
      return resource as ResourceUsage;
    }
    return null;
  };

  const openUpgradeModal = (resource = '') => {
    setUpgradeModalResource(resource);
    setShowUpgradeModal(true);
  };

  const openCustomDomainModal = () => {
    setShowCustomDomainModal(true);
  };

  const adjustUsage = useCallback((resourceType: keyof PlanUsage, delta: number) => {
    setPlanInfo(prev => {
      if (!prev?.usage) return prev;
      const resource = prev.usage[resourceType];
      if (!('current' in resource)) return prev;
      const updated = { ...resource } as ResourceUsage;
      updated.current = Math.max(0, updated.current + delta);
      if (updated.limit !== -1) {
        updated.canAdd = updated.current < updated.limit;
      }
      return { ...prev, usage: { ...prev.usage, [resourceType]: updated } };
    });
  }, []);

  const value: PlanContextType = {
    planInfo,
    loading,
    error,
    isPaidPlan,
    isUnpaid,
    isPro,
    isProMax,
    lifecycle: planInfo?.lifecycle || null,
    capabilities,
    isInGracePeriod,
    isSuperAdmin,
    canAdd,
    getUsage,
    adjustUsage,
    isItemVisibleOnWebsite,
    hasContactLeadsAccess,
    hasVisitorAnalyticsAccess,
    refreshPlanInfo: fetchPlanInfo,
    showUpgradeModal,
    setShowUpgradeModal,
    upgradeModalResource,
    openUpgradeModal,
    showCustomDomainModal,
    setShowCustomDomainModal,
    openCustomDomainModal,
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
};

export const usePlan = (): PlanContextType => {
  const context = useContext(PlanContext);
  if (!context) {
    // Return a default context if not within provider (for super-admin without domain)
    return {
      planInfo: null,
      loading: false,
      error: null,
      isPaidPlan: true,
      isUnpaid: false,
      isPro: false,
      isProMax: true,
      lifecycle: null,
      capabilities: {
        websiteLayoutsAccess: true,
        customDomainAccess: true,
        managerInvitesAccess: true,
        contactLeadsAccess: true,
        visitorAnalyticsAccess: true,
        customCategoriesAccess: true,
      },
      isInGracePeriod: false,
      isSuperAdmin: true,
      canAdd: () => true,
      getUsage: () => null,
      adjustUsage: () => {},
      isItemVisibleOnWebsite: () => true,
      hasContactLeadsAccess: true,
      hasVisitorAnalyticsAccess: true,
      refreshPlanInfo: () => {},
      showUpgradeModal: false,
      setShowUpgradeModal: () => {},
      upgradeModalResource: '',
      openUpgradeModal: () => {},
      showCustomDomainModal: false,
      setShowCustomDomainModal: () => {},
      openCustomDomainModal: () => {},
    };
  }
  return context;
};

export default PlanContext;
